import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { BaseStyleClassesDirective, FieldId } from '@gk/gk-modules';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import {
  filter,
  isObservable,
  map,
  Observable,
  of,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs';
import {
  BsMessageType,
  maxWorkspaceStateSizeInBytes,
  WorkspaceState,
  WorkspaceStateDraftVersion,
  WorkspaceStateId,
} from '../../services/request-workspace-state/request-workspace-state.model';
import { RequestWorkspaceStateService } from '../../services/request-workspace-state/request-workspace-state.service';
import { getJsonMemorySize } from '../../utils/json.util';
@Component({
  selector: 'app-workspace-state',
  templateUrl: './workspace-state.component.html',
  styleUrl: './workspace-state.component.scss',
  standalone: false,
})
export class WorkspaceStateComponent<TWorkspaceState extends WorkspaceState>
  extends BaseStyleClassesDirective
  implements OnInit, OnDestroy
{
  private isAlive = true;
  workspaceStateTranslations: { [key: string]: string } = {};
  workspaceStateLoading = false;
  workspaceMsgType: BsMessageType;
  workspaceMsgTxt = '';
  @Input() workspaceStateDraftVersion: WorkspaceStateDraftVersion;
  @Input() workspaceStateId: WorkspaceStateId;
  @Input() getWorkspaceStateToSave: () =>
    | Observable<TWorkspaceState>
    | TWorkspaceState;
  @Input() workspaceStateSaveValidator: () => boolean | Observable<boolean>;
  @Input() workspaceStateLoadValidator: () => boolean | Observable<boolean>;
  @Input() requestMode?: FieldId;
  @Output() workspaceStateLoaded = new EventEmitter<TWorkspaceState>();

  constructor(
    private requestWorkspaceStateService: RequestWorkspaceStateService,
    private translateService: TranslateService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToWorkspaceStateTranslations();
  }

  subscribeToWorkspaceStateTranslations(): void {
    this.translateService
      .get('WORKSPACE_STATE')
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(
        (translations) => (this.workspaceStateTranslations = translations),
      );
  }

  setWorkspaceMsg(type: BsMessageType, text: string): void {
    this.workspaceMsgType = type;
    this.workspaceMsgTxt = text;
  }

  setWorkspaceLoading(translationKey: string): void {
    this.setWorkspaceMsg(
      BsMessageType.Info,
      this.workspaceStateTranslations[translationKey],
    );
    this.workspaceStateLoading = true;
  }

  handleWorkspaceJobDone(success: boolean, translationKey?: string): void {
    if (translationKey) {
      this.setWorkspaceMsg(
        success ? BsMessageType.Success : BsMessageType.Danger,
        this.workspaceStateTranslations[translationKey],
      );
    }
    this.workspaceStateLoading = false;
  }

  hasWorkspaceProperMemorySize(workspace: TWorkspaceState): boolean {
    const jsonSize = getJsonMemorySize(_.cloneDeep(workspace));

    return jsonSize <= maxWorkspaceStateSizeInBytes;
  }

  loadWorkspaceState(): void {
    this.setWorkspaceLoading('DOWNLOADING');
    const workspaceStateLoadValidator = this.workspaceStateLoadValidator
      ? this.workspaceStateLoadValidator()
      : true;
    (isObservable(workspaceStateLoadValidator)
      ? workspaceStateLoadValidator
      : of(workspaceStateLoadValidator)
    )
      .pipe(
        tap((isValid) => {
          if (!isValid) {
            this.resetWorkspaceActionInfo();
          }
        }),
        filter((isValid) => isValid),
        takeWhile(() => this.isAlive),
        switchMap(() =>
          this.requestWorkspaceStateService.getWorkspaceState<TWorkspaceState>(
            this.workspaceStateId,
            this.requestMode,
          ),
        ),
      )
      .subscribe({
        next: (state) => {
          try {
            this.handleLoadWorkspaceResponse(state);
          } catch {
            this.handleWorkspaceJobDone(false, 'NOT_COMPATIBLE');
          }
        },
        error: () => this.handleWorkspaceJobDone(false, 'ERROR_OCCURED'),
      });
  }

  handleLoadWorkspaceResponse(state: TWorkspaceState): void {
    if (!state) {
      this.handleWorkspaceJobDone(false, 'EMPTY_WORKSPACE');
      return;
    }
    if (state.draftVersion !== this.workspaceStateDraftVersion) {
      this.handleWorkspaceJobDone(false, 'NOT_COMPATIBLE');
      return;
    }
    this.workspaceStateLoaded.emit(state);
    this.handleWorkspaceJobDone(true, 'LOADED_SUCCEED');
  }

  saveWorkspaceState(): void {
    this.setWorkspaceLoading('UPLOADING');
    const workspaceToSaveResult = this.getWorkspaceStateToSave();
    const workspaceStateSaveValidator = this.workspaceStateSaveValidator
      ? this.workspaceStateSaveValidator()
      : true;
    (isObservable(workspaceStateSaveValidator)
      ? workspaceStateSaveValidator
      : of(workspaceStateSaveValidator)
    )
      .pipe(
        tap((isValid) => {
          if (!isValid) {
            this.resetWorkspaceActionInfo();
          }
        }),
        filter((isValid) => isValid),
        takeWhile(() => this.isAlive),
        switchMap(() =>
          isObservable(workspaceToSaveResult)
            ? workspaceToSaveResult
            : of(workspaceToSaveResult),
        ),
        map((workspace) => ({
          draftVersion: this.workspaceStateDraftVersion,
          ...workspace,
        })),
      )
      .subscribe({
        next: (workspaceToSave) => {
          if (!this.hasWorkspaceProperMemorySize(workspaceToSave)) {
            this.handleWorkspaceJobDone(false, 'TOO_BIG');
            return;
          }
          this.requestWorkspaceStateService
            .updateWorkspaceState(
              this.workspaceStateId,
              workspaceToSave,
              this.requestMode,
            )
            .subscribe({
              next: () => this.handleWorkspaceJobDone(true, 'UPLOADED_SUCCEED'),
              error: () => this.handleWorkspaceJobDone(false, 'ERROR_OCCURED'),
            });
        },
        error: (error) => {
          console.error('Error while saving workspace state:', error);
        },
      });
  }

  resetWorkspaceActionInfo(): void {
    this.workspaceMsgType = undefined;
    this.workspaceMsgTxt = undefined;
    this.workspaceStateLoading = false;
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
